import {
  AddInError,
  getIsOnPremises,
  handleError,
  logError,
  ReportAction,
  type ReportResponseType,
} from '@utils-global';
import {
  deleteMail,
  deleteMailEWS,
  getMailParentFolderId,
  moveMailToSpam,
  moveMailToSpamEWS,
} from '@data';
import { metrics } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { PostReportActionError } from './postReportActionError';
import type { MailIdKind } from './resolveActionMailId';

export enum Backend {
  EWS = 'ews',
  GRAPH = 'graph',
}

/**
 * Whether a post-report action actually relocated the mail.
 *
 * `UNDETERMINED` is deliberately distinct from `NOT_MOVED`: some responses tell
 * us nothing at all, a Graph delete answers `204` with an empty body.
 */
export enum MoveStatus {
  MOVED = 'moved',
  NOT_MOVED = 'not_moved',
  UNDETERMINED = 'undetermined',
}

/**
 * Compares an identity taken before the action with the one after it: folder ids
 * on Graph, item ids on EWS. Either side missing means there was nothing to
 * compare against.
 */
const determineMoveStatus = (before?: string | null, after?: string | null): MoveStatus => {
  if (!before || !after) {
    return MoveStatus.UNDETERMINED;
  }

  return before === after ? MoveStatus.NOT_MOVED : MoveStatus.MOVED;
};

/**
 * A move mints a new `ItemId` and invalidates the old one, so an id differing from the one we sent
 * is proof the item changed folder.
 */
const performEwsReportAction = async (
  action: ReportAction,
  mailId: string,
  isSharedMailbox: boolean,
  mailIdKind?: MailIdKind,
  reportResponseType?: ReportResponseType,
) => {
  const result =
    action === ReportAction.SPAM
      ? await moveMailToSpamEWS(mailId, isSharedMailbox)
      : await deleteMailEWS(mailId, isSharedMailbox);

  Sentry.logger.info('Post-report action completed', {
    action: ReportAction[action],
    reportResponseType,
    isSharedMailbox,
    backend: Backend.EWS,
    originalMailId: mailId,
    mailIdKind,
    movedMailId: result.movedMailId,
    moveStatus: determineMoveStatus(mailId, result.movedMailId),
  });
};

/**
 * A move returns the mail's new `parentFolderId` in the response, read the folder it resided in
 * beforehand and compare them.
 *
 * IMPORTANT: A delete answers `204` with an empty body and stays undetermined.
 */
const performGraphReportAction = async (
  action: ReportAction,
  mailId: string,
  userEmail: string,
  isSharedMailbox: boolean,
  mailIdKind?: MailIdKind,
  reportResponseType?: ReportResponseType,
) => {
  const previousParentFolderId =
    action === ReportAction.SPAM ? await getMailParentFolderId(mailId, userEmail) : null;

  const response =
    action === ReportAction.SPAM
      ? await moveMailToSpam(mailId, userEmail, isSharedMailbox)
      : await deleteMail(mailId, userEmail, isSharedMailbox);

  const parentFolderId = response?.data?.parentFolderId;

  Sentry.logger.info('Post-report action completed', {
    action: ReportAction[action],
    reportResponseType,
    isSharedMailbox,
    backend: Backend.GRAPH,
    originalMailId: mailId,
    mailIdKind,
    previousParentFolderId,
    parentFolderId,
    moveStatus: determineMoveStatus(previousParentFolderId, parentFolderId),
  });
};

/**
 * How the post-report action ended. Carried as a dimension on the flow timing metric, so an
 * action that was skipped or failed never blends into the durations of the ones that actually
 * relocated a mail — a Graph move that retries before giving up is slow in a way a successful
 * one is not.
 */
export enum PostReportActionOutcome {
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  NO_MAIL_ID = 'no_mail_id',
  MISSING_FOLDER = 'missing_folder',
  FAILED = 'failed',
}

const actionNotificationMap = {
  [ReportAction.SPAM]: {
    error: 'errors.moveToSpam',
    missingFolder: 'errors.spamFolderNotFound',
  },
  [ReportAction.DELETE]: {
    error: 'errors.deleteEmail',
    // On-premises a delete is a move to deleteditems and can fail the same way, but there is no
    // folder the user would recognize to name, so the generic error fits.
    missingFolder: undefined,
  },
  [ReportAction.NOTHING]: { info: '', error: '', missingFolder: undefined },
};

export const handleReportResultAction = async (
  action: ReportAction,
  mailId: string | undefined,
  userEmail: string,
  showToast: (translationKey: string, options?: any) => void,
  isSharedMailbox: boolean = false,
  {
    mailIdKind,
    reportResponseType,
  }: {
    mailIdKind?: MailIdKind;
    reportResponseType?: ReportResponseType;
  } = {},
): Promise<PostReportActionOutcome> => {
  const notifications = actionNotificationMap[action];
  const isOnPremises = getIsOnPremises();

  if (action === ReportAction.NOTHING) {
    Sentry.logger.info('Post-report action skipped, configured to do nothing', {
      action: ReportAction[action],
      reportResponseType,
      backend: isOnPremises ? Backend.EWS : Backend.GRAPH,
      isSharedMailbox,
      mailIdKind,
    });
    return PostReportActionOutcome.SKIPPED;
  }

  if (!mailId) {
    logError('Post-report action skipped, no mail id available', { action, reportResponseType });
    showToast(notifications.error);
    return PostReportActionOutcome.NO_MAIL_ID;
  }

  try {
    await (isOnPremises
      ? performEwsReportAction(action, mailId, isSharedMailbox, mailIdKind, reportResponseType)
      : performGraphReportAction(
          action,
          mailId,
          userEmail,
          isSharedMailbox,
          mailIdKind,
          reportResponseType,
        ));

    return PostReportActionOutcome.COMPLETED;
  } catch (error) {
    const backend = isOnPremises ? Backend.EWS : Backend.GRAPH;
    const context = error instanceof AddInError ? error.context : undefined;

    // The mailbox has no folder to move to. Nothing to fix on our side and the toast says what is
    // wrong, so it is counted rather than raised as an error to triage.
    if (context?.missingFolder === true) {
      metrics.count('post_report_action.missing_folder', 1, {
        attributes: { action: ReportAction[action], backend, isSharedMailbox },
      });
      Sentry.logger.warn('Post-report action skipped, mailbox has no destination folder', {
        action: ReportAction[action],
        reportResponseType,
        backend,
        isSharedMailbox,
        mailIdKind,
      });
      showToast(notifications.missingFolder ?? notifications.error);
      return PostReportActionOutcome.MISSING_FOLDER;
    }

    const postReportActionError = new PostReportActionError(
      `Post-report action failed (${backend})`,
      {
        action: ReportAction[action],
        reportResponseType,
        backend,
        mailId,
        mailIdKind,
        isSharedMailbox,
        ...context,
      },
      error,
    );

    handleError(postReportActionError, () => showToast(notifications.error));

    return PostReportActionOutcome.FAILED;
  } finally {
    // Flushed but not awaited: the result screen is gated on this function returning, so
    // awaiting a Sentry round-trip would keep the user on the loading screen for it. The
    // browser transport already sets `keepalive` on the request, so it survives the task
    // pane being dismissed mid-send without us holding the UI hostage to it.
    Sentry.flush(2000).catch(() => {});
  }
};
